import {
  SUBSCRIPTION_PRODUCT_TYPE,
  MAGAZINE_SINGLE_ISSUE_PRODUCT_TYPE,
  SUBSCRIPTION_TYPE_OPTION_LABEL,
  REGION_OPTION_LABEL,
  OFFER_PARAM,
  CAMPAIGN_CODE_FIELD,
  OFFER_OPTION_LABEL,
  STANDARD_OFFER_VALUE
} from './constants';
import { getQueryParam } from '../helpers/query-params';

const getProductType = product => product?.productType || null;

const isSubscriptionProduct = product => getProductType(product) === SUBSCRIPTION_PRODUCT_TYPE;

const isSingleIssueProduct = product =>
  getProductType(product) === MAGAZINE_SINGLE_ISSUE_PRODUCT_TYPE;

/**
 * Returns an object keyed by option label, each containing a list of unique { valueName, valueId } entries
 * across all variants. Used for building multi-attribute select dropdowns.
 */
const getOptionGroups = variants => {
  if (!variants?.length) return {};
  const groups = {};
  variants.forEach(({ attributes: { selectedOptions = [] } = {} }) => {
    selectedOptions.forEach(({ optionLabel, valueName, valueId, optionId }) => {
      if (!groups[optionLabel]) groups[optionLabel] = { optionId, values: [] };
      const exists = groups[optionLabel].values.find(v => v.valueId === valueId);
      if (!exists) groups[optionLabel].values.push({ valueName, valueId });
    });
  });
  return groups;
};

/**
 * Groups variants by their "Subscription Type" selected option value.
 * Returns an object keyed by subscription type name (e.g. "Print + Online"),
 * each containing an array of matching variants.
 */
const getSubscriptionTypeGroups = variants => {
  if (!variants?.length) return {};
  const groups = {};
  variants.forEach(variant => {
    const { attributes: { selectedOptions = [] } = {} } = variant;
    const subscriptionTypeOption = selectedOptions.find(
      opt => opt.optionLabel === SUBSCRIPTION_TYPE_OPTION_LABEL
    );
    if (subscriptionTypeOption) {
      const { valueName } = subscriptionTypeOption;
      if (!groups[valueName]) groups[valueName] = [];
      groups[valueName].push(variant);
    }
  });
  return Object.fromEntries(Object.entries(groups).sort(([a], [b]) => a.length - b.length));
};

/**
 * Returns the first variant whose selectedOptions match all provided selections.
 * Falls back to the first variant if no match is found.
 */
const getVariantForSelections = (variants, selections) => {
  if (!variants?.length) return null;
  return (
    variants.find(({ attributes: { selectedOptions = [] } = {} }) =>
      Object.entries(selections).every(([label, value]) =>
        selectedOptions.some(opt => opt.optionLabel === label && opt.valueName === value)
      )
    ) || variants[0]
  );
};

/**
 * Returns true if any variant has at least one selectedOption.
 */
const hasAttributes = variants => {
  if (!variants?.length) return false;
  return variants.some(
    ({ attributes: { selectedOptions = [] } = {} }) => selectedOptions.length > 0
  );
};

/**
 * Returns unique Region option entries (valueName, valueId) across all variants in a tab group.
 * Used to build the region select within a subscription type tab.
 */
const getRegionOptions = variants => {
  if (!variants?.length) return [];
  const seen = new Set();
  const options = [];
  variants.forEach(({ attributes: { selectedOptions = [] } = {} }) => {
    const regionOpt = selectedOptions.find(opt => opt.optionLabel === REGION_OPTION_LABEL);
    if (regionOpt && !seen.has(regionOpt.valueId)) {
      seen.add(regionOpt.valueId);
      options.push({ valueName: regionOpt.valueName, valueId: regionOpt.valueId });
    }
  });
  return options.sort((a, b) => a.valueName.length - b.valueName.length);
};

/**
 * Filters variants to those matching a given Region valueName.
 * Returns all variants unchanged when regionName is falsy.
 */
const getVariantsForRegion = (variants, regionName) => {
  if (!variants?.length) return [];
  if (!regionName) return variants;
  return variants.filter(({ attributes: { selectedOptions = [] } = {} }) =>
    selectedOptions.some(
      opt => opt.optionLabel === REGION_OPTION_LABEL && opt.valueName === regionName
    )
  );
};

/**
 * Reads the offer query string value from a Next.js router asPath.
 * Returns null when the path has no offer param.
 */
const getOfferFromPath = (asPath = '') => getQueryParam(asPath, OFFER_PARAM);

const getCampaignCode = variant => variant?.attributes?.customFields?.[CAMPAIGN_CODE_FIELD] || null;

const normaliseCode = code => String(code).toLowerCase();

const getOfferOptionValue = ({ attributes: { selectedOptions = [] } = {} } = {}) =>
  selectedOptions.find(opt => opt.optionLabel === OFFER_OPTION_LABEL)?.valueName || null;

/**
 * True when the variant is tied to an offer: it has a campaign code, or an "Offer" option
 * whose value isn't STANDARD (which marks a variant as "no offer").
 */
const isOfferVariant = variant => {
  const offerOptionValue = getOfferOptionValue(variant);
  return (
    !!getCampaignCode(variant) ||
    (!!offerOptionValue && normaliseCode(offerOptionValue) !== normaliseCode(STANDARD_OFFER_VALUE))
  );
};

/**
 * True when the variant's campaign code or "Offer" option value contains the offer, ignoring
 * case. Alt campaign codes are variations built on the unique offer code from the URL, e.g. an
 * offer of XMAS26 matches XMAS26, XMAS26-12M and ALT-XMAS26-UK.
 */
const matchesOffer = (variant, offer) => {
  const normalisedOffer = normaliseCode(offer);
  if (!normalisedOffer) return false;
  return [getCampaignCode(variant), getOfferOptionValue(variant)].some(
    code => !!code && normaliseCode(code).includes(normalisedOffer)
  );
};

/**
 * True when at least one variant matches the offer (see matchesOffer).
 */
const hasOfferVariants = (variants, offer) =>
  !!offer && !!variants?.some(variant => matchesOffer(variant, offer));

/**
 * Turns an offer code into a single class-name-safe token: lowercased (matching is
 * case-insensitive, so XMAS26 and xmas26 share a class) with any run of characters other
 * than a-z, 0-9, "-" and "_" replaced by "-". Returns an empty string when nothing is left.
 */
const getOfferClassToken = offer =>
  offer
    ? String(offer)
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, '-')
        .replace(/^-+|-+$/g, '')
    : '';

/**
 * Filters variants against the offer from the URL.
 * With an offer: returns variants whose campaign code or "Offer" option contains it, or all
 * variants when none match.
 * Without an offer: returns only variants that aren't tied to an offer (see isOfferVariant).
 */
const filterVariantsByOffer = (variants, offer) => {
  if (!variants?.length) return [];
  if (!offer) return variants.filter(variant => !isOfferVariant(variant));
  const offerVariants = variants.filter(variant => matchesOffer(variant, offer));
  return offerVariants.length ? offerVariants : variants;
};

// GraphQL's Int is a signed 32-bit integer; anything larger fails validation for the whole query
const GRAPHQL_INT_MAX = 2147483647;

/**
 * Returns a product's darwinProductId as an integer, or null when it is missing or not a
 * positive whole number that fits a GraphQL Int. It may arrive as a number (48) or a string
 * ("48").
 */
const parseDarwinProductId = value => {
  if (value == null || value === '') return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 && id <= GRAPHQL_INT_MAX ? id : null;
};

/**
 * Returns 25 December of the current year as YYYY-MM-DD. Issues selected after this date
 * count as "after Christmas".
 */
const getChristmasDate = (now = new Date()) => `${now.getFullYear()}-12-25`;

export {
  getProductType,
  isSubscriptionProduct,
  isSingleIssueProduct,
  getOptionGroups,
  getSubscriptionTypeGroups,
  getVariantForSelections,
  hasAttributes,
  getRegionOptions,
  getVariantsForRegion,
  getOfferFromPath,
  getCampaignCode,
  getOfferOptionValue,
  isOfferVariant,
  matchesOffer,
  hasOfferVariants,
  getOfferClassToken,
  filterVariantsByOffer,
  parseDarwinProductId,
  getChristmasDate
};
